import React, { useEffect } from 'react';
import { useData } from '../../contexts/DataContext';
import { useAuth } from '../../contexts/AuthContext';
import { Card } from '../../components/cards/Card';
import { Button } from '../../components/buttons/Button';
import { Bell, CheckCircle, Info, AlertTriangle, XCircle, Trash2, CheckSquare } from 'lucide-react';

export const NotificationsPage: React.FC = () => {
    const { notifications, markNotificationRead, refreshData } = useData();
    const { user } = useAuth();

    useEffect(() => {
        refreshData();
    }, []);

    const getIcon = (type: string) => {
        switch (type) {
            case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />;
            case 'warning': return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
            case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
            default: return <Info className="w-5 h-5 text-blue-500" />;
        }
    };

    const getTimeAgo = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

        if (diffInSeconds < 60) return 'just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        return date.toLocaleDateString();
    };

    const handleMarkAllRead = async () => {
        const unread = notifications.filter(n => !n.read);
        for (const n of unread) {
            await markNotificationRead(n.id);
        }
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-[#2f5c96] rounded-xl flex items-center justify-center text-white shadow-lg shadow-blue-200">
                            <Bell className="w-6 h-6" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
                            <p className="text-sm text-gray-500">Stay updated with your property alerts</p>
                        </div>
                    </div>

                    {notifications.some(n => !n.read) && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleMarkAllRead}
                            className="hidden sm:flex items-center gap-2"
                        >
                            <CheckSquare className="w-4 h-4" />
                            Mark all as read
                        </Button>
                    )}
                </div>

                <div className="space-y-4">
                    {notifications.length > 0 ? (
                        notifications.map((notification) => (
                            <Card
                                key={notification.id}
                                className={`p-5 transition-all hover:shadow-md border-l-4 ${notification.read ? 'border-transparent bg-white' : 'border-[#2f5c96] bg-blue-50/30'}`}
                            >
                                <div className="flex gap-4">
                                    <div className={`mt-1 flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${notification.read ? 'bg-gray-100' : 'bg-white shadow-sm border border-blue-100'}`}>
                                        {getIcon(notification.type)}
                                    </div>
                                    <div className="flex-grow">
                                        <div className="flex justify-between items-start mb-1">
                                            <p className={`text-sm ${notification.read ? 'text-gray-600' : 'font-semibold text-gray-900'}`}>
                                                {notification.message}
                                            </p>
                                            <span className="text-xs text-gray-400 whitespace-nowrap ml-4">
                                                {getTimeAgo(notification.date)}
                                            </span>
                                        </div>
                                        {!notification.read && (
                                            <button
                                                onClick={() => markNotificationRead(notification.id)}
                                                className="text-xs font-semibold text-[#2f5c96] hover:text-[#254a7a] mt-2 inline-flex items-center gap-1"
                                            >
                                                Mark as read
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        ))
                    ) : (
                        <Card className="p-12 text-center">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Bell className="w-8 h-8 text-gray-300" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">No notifications yet</h3>
                            <p className="text-gray-500">We'll notify you when interesting things happen.</p>
                        </Card>
                    )}
                </div>

                {/* Mobile FAB for actions */}
                {notifications.some(n => !n.read) && (
                    <div className="sm:hidden fixed bottom-24 right-6">
                        <button
                            onClick={handleMarkAllRead}
                            className="w-14 h-14 bg-[#2f5c96] text-white rounded-full shadow-xl flex items-center justify-center hover:bg-[#254a7a] active:scale-95 transition-all"
                            title="Mark all as read"
                        >
                            <CheckSquare className="w-6 h-6" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};
